import styled from 'styled-components';
import {
  Box,
  Flex,
  H2,
  P,
  Typography,
  Pill,
  spacing,
  colors,
} from '@procore/core-react';
import { UnstyledLink } from '@/react/styles';
import { marketplaceColors } from '@/react/styles/constants';

export const CardHeader = styled(Flex)`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
`;

export const CardBody = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  height: 120px;
`;

export const CardTitle = styled(H2)`
  color: ${colors.black};
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardTagline = styled(P)`
  color: ${colors.black};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  margin: 0;
  letter-spacing: 0.64px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardTypography = styled(Typography)`
  display: inline-block;
  color: ${marketplaceColors.concrete50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.64px;
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const CardFooter = styled(Box)`
  padding-top: ${spacing.lg}px;
  border-top: 1px solid ${marketplaceColors.scale50};
  height: 75px;
  overflow: hidden;
`;

export const FooterDescription = styled(P)`
  color: ${marketplaceColors.concrete50};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.64px;
  margin: 0;
  height: 100%;
`;

export const CardLink = styled(UnstyledLink)`
  display: block;
  width: 100%;
  min-width: 0; /* Allow grid item to shrink below content size */

  &:focus {
    outline: none;
    box-shadow: none;
  }

  & > * {
    border-radius: 0;
    border: 1px solid ${marketplaceColors.scale50};
    border-top: none;
    border-left: none;
    box-shadow: none;
  }

  /* Desktop layout (3+ columns) */
  /* First 3 cards get top border */
  &:nth-child(-n + 3) > * {
    border-top: 1px solid ${marketplaceColors.scale50};
  }

  /* First card and every 3rd+1 card (row starters) get left border */
  &:nth-child(3n + 1) > * {
    border-left: 1px solid ${marketplaceColors.scale50};
  }

  /* Border radius for corner cards in 3-column layout */
  /* Top-left corner (first card) */
  &:first-child > * {
    border-top-left-radius: 4px;
  }

  /* Top-right corner (3rd card in first row, or last card if fewer than 3) */
  &:nth-child(3) > *,
  &:last-child:nth-child(-n + 3) > * {
    border-top-right-radius: 4px;
  }

  /* Bottom-left corner (first card in last row) */
  &:nth-last-child(-n + 3):nth-child(3n + 1) > * {
    border-bottom-left-radius: 4px;
  }

  /* Bottom-right corner (last card, or rightmost card in second-to-last row if last row is incomplete) */
  &:last-child > *,
  &:nth-child(3n):nth-last-child(2) > *,
  &:nth-child(3n):nth-last-child(-n + 3) > * {
    border-bottom-right-radius: 4px;
  }

  @media (max-width: 1024px) {
    /* Tablet layout (2 columns) */
    /* Reset desktop top borders */
    &:nth-child(-n + 3) > * {
      border-top: none;
    }

    /* Reset desktop left borders */
    &:nth-child(3n + 1) > * {
      border-left: none;
    }

    /* Reset desktop border radius */
    &:nth-child(3) > *,
    &:last-child:nth-child(-n + 3) > *,
    &:nth-last-child(-n + 3):nth-child(3n + 1) > * {
      border-top-right-radius: 0;
      border-bottom-left-radius: 0;
    }

    /* First 2 cards get top border */
    &:nth-child(-n + 2) > * {
      border-top: 1px solid ${marketplaceColors.scale50};
    }

    /* Every odd card (1st, 3rd, 5th...) gets left border */
    &:nth-child(odd) > * {
      border-left: 1px solid ${marketplaceColors.scale50};
    }

    /* Border radius for corner cards in 2-column layout */
    /* Top-right corner (2nd card in first row, or last card if only 1) */
    &:nth-child(2) > *,
    &:last-child:first-child > * {
      border-top-right-radius: 4px;
    }

    /* Bottom-left corner (first card in last row) */
    &:nth-last-child(-n + 2):nth-child(odd) > * {
      border-bottom-left-radius: 4px;
    }

    /* Bottom-right corner (last card, or rightmost card in second-to-last row if last row is incomplete) */
    &:nth-child(2n):nth-last-child(2) > * {
      border-bottom-right-radius: 4px;
    }
  }

  @media (max-width: 768px) {
    /* Mobile layout (1 column) */
    /* Reset tablet borders */
    &:nth-child(-n + 2) > * {
      border-top: none;
    }

    &:nth-child(3n + 1) > * {
      border-left: 1px solid ${marketplaceColors.scale50};
    }

    /* Reset tablet border radius */
    &:nth-child(2) > *,
    &:last-child:first-child > *,
    &:nth-last-child(-n + 2):nth-child(odd) > * {
      border-top-right-radius: 0;
      border-bottom-left-radius: 0;
    }

    /* All cards get full border (left, right, bottom) */
    & > * {
      border-left: 1px solid ${marketplaceColors.scale50};
      border-right: 1px solid ${marketplaceColors.scale50};
      border-bottom: 1px solid ${marketplaceColors.scale50};
    }

    /* First card gets top border */
    &:first-child > * {
      border-top: 1px solid ${marketplaceColors.scale50};
    }

    /* Border radius for corner cards in 1-column layout */
    /* Top corners (first card) */
    &:first-child > * {
      border-top-left-radius: 4px;
      border-top-right-radius: 4px;
    }

    /* Bottom corners (last card) */
    &:last-child > * {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }
  }
`;

export const Card = styled(Box)`
  height: 321px;
  width: 100%;
  transition: all 0.25s ease;

  &:hover,
  &:focus {
    border-color: ${marketplaceColors.scale50};
    background: ${marketplaceColors.stone40};
    outline: none;
  }
`;

export const SecurityBadgePill = styled(Pill)`
  height: 24px;
  border-radius: 100px;

  svg {
    width: 18px;
    height: 20px;
  }
`;
